import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Clapperboard, History, Pause, Play } from "lucide-react";
import { api } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { Cover } from "@/components/Cover";
import { fmtLong } from "@/pages/Audiobooks";
import { VideoThumb, epCode, fmtClock } from "@/components/VideoBits";

const useContinue = () => {
  const p = usePlayer();
  const [book, setBook] = useState(null);
  const [video, setVideo] = useState(null);
  useEffect(() => { api.get("/audiobooks/continue").then((r) => setBook(r.data.book)).catch(() => setBook(null)); }, [p.current?.album_id]);
  useEffect(() => { api.get("/videos/continue").then((r) => setVideo(r.data.video)).catch(() => setVideo(null)); }, []);
  return { book, video };
};

const chip = "flex h-10 min-w-0 max-w-[320px] flex-1 items-center gap-2 rounded-full border border-zinc-300/80 bg-white/80 py-1 pl-1 pr-1 shadow-sm backdrop-blur";
const resumeBtn = "flex h-8 shrink-0 items-center gap-1 rounded-full bg-zinc-900 px-3 text-[11px] font-medium text-white transition-transform hover:scale-[1.04] active:scale-95";

const BookChip = ({ book }) => {
  const p = usePlayer();
  const pr = book.progress;
  const left = Math.max(0, (pr.total_duration || 0) - (pr.elapsed || 0));
  const isCurrent = p.current?.album_id === book.id && p.current?.is_audiobook;
  const resume = () => {
    if (isCurrent) { p.toggle(); return; }
    if (p.shuffle) p.setShuffle(false);
    const idx = Math.max(0, book.tracks.findIndex((t) => t.id === pr.track_id));
    p.playTracks(book.tracks, idx, pr.position || 0);
  };
  return (
    <div className={chip} data-testid="continue-book" title={`${book.name} — ${book.artist}`}>
      <Link to={`/audiobooks/${book.id}`} className="shrink-0" data-testid="continue-listening-cover"><Cover albumId={book.id} hasCover={book.has_cover} version={book.cover_version} className="h-8 w-8" rounded="rounded-full" iconSize={12} /></Link>
      <Link to={`/audiobooks/${book.id}`} className="min-w-0 flex-1 leading-tight">
        <span className="block truncate text-[9px] font-semibold uppercase tracking-wide text-zinc-400"><BookOpen size={9} className="mr-1 inline-block align-[-1px]" />Audiobook · {Math.round(pr.pct)}% · {fmtLong(left)} left</span>
        <span className="block truncate text-xs font-medium text-zinc-900" data-testid="continue-listening-title">{book.name}</span>
      </Link>
      <button onClick={resume} className={resumeBtn} data-testid="continue-listening-resume"><Play size={11} fill="currentColor" />{isCurrent && p.playing ? "Pause" : "Resume"}</button>
    </div>
  );
};

const VideoChip = ({ v }) => {
  const nav = useNavigate();
  const pr = v.progress;
  const left = Math.max(0, (v.duration || 0) - (pr.position || 0));
  return (
    <div className={chip} data-testid="continue-video" title={v.title}>
      <Link to={`/videos/watch/${v.id}`} className="w-14 shrink-0" data-testid="continue-video-thumb"><VideoThumb id={v.id} hasThumb={v.has_thumb} className="!h-8 !w-14 !rounded-full" /></Link>
      <Link to={`/videos/watch/${v.id}`} className="min-w-0 flex-1 leading-tight">
        <span className="block truncate text-[9px] font-semibold uppercase tracking-wide text-zinc-400"><Clapperboard size={9} className="mr-1 inline-block align-[-1px]" />Video{epCode(v) ? ` · ${epCode(v)}` : ""} · {Math.round(pr.pct)}%{v.duration ? ` · ${fmtClock(left)} left` : ""}</span>
        <span className="block truncate text-xs font-medium text-zinc-900" data-testid="continue-video-title">{v.title}</span>
      </Link>
      <button onClick={() => nav(`/videos/watch/${v.id}`)} className={resumeBtn} data-testid="continue-video-resume"><Play size={11} fill="currentColor" />Resume</button>
    </div>
  );
};

/* Fixed-height "continue" chips for the top bar (phones only — the sidebar has them on wider screens). */
export const ContinueChips = () => {
  const { book, video } = useContinue();
  if (!book && !video) return null;
  return (
    <div className="flex min-w-0 flex-1 items-center justify-center gap-2 overflow-hidden md:hidden" data-testid="continue-listening">
      {book && <BookChip book={book} />}
      {video && <VideoChip v={video} />}
    </div>
  );
};

const row = "group flex items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors hover:bg-zinc-100";
const rowBtn = "flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-white transition-transform hover:scale-[1.06] active:scale-95";

const BookRow = ({ book }) => {
  const p = usePlayer();
  const pr = book.progress;
  const left = Math.max(0, (pr.total_duration || 0) - (pr.elapsed || 0));
  const isCurrent = p.current?.album_id === book.id && p.current?.is_audiobook;
  const resume = () => {
    if (isCurrent) { p.toggle(); return; }
    if (p.shuffle) p.setShuffle(false);
    const idx = Math.max(0, book.tracks.findIndex((t) => t.id === pr.track_id));
    p.playTracks(book.tracks, idx, pr.position || 0);
  };
  return (
    <div className={row} data-testid="sidebar-resume-book" title={`${book.name} — ${book.artist}`}>
      <Link to={`/audiobooks/${book.id}`} className="shrink-0" data-testid="sidebar-resume-book-cover"><Cover albumId={book.id} hasCover={book.has_cover} version={book.cover_version} className="h-9 w-9" rounded="rounded-md" iconSize={12} /></Link>
      <Link to={`/audiobooks/${book.id}`} className="min-w-0 flex-1 leading-tight">
        <span className="block truncate text-[9px] font-semibold uppercase tracking-wide text-zinc-400"><BookOpen size={9} className="mr-1 inline-block align-[-1px]" />{Math.round(pr.pct)}% · {fmtLong(left)} left</span>
        <span className="block truncate text-xs font-medium text-zinc-900" data-testid="sidebar-resume-book-title">{book.name}</span>
      </Link>
      <button onClick={resume} className={rowBtn} aria-label={isCurrent && p.playing ? "Pause" : "Resume"} title={isCurrent && p.playing ? "Pause" : "Resume"} data-testid="sidebar-resume-book-button">
        {isCurrent && p.playing ? <Pause size={11} fill="currentColor" /> : <Play size={11} fill="currentColor" />}
      </button>
    </div>
  );
};

const VideoRow = ({ v }) => {
  const nav = useNavigate();
  const pr = v.progress;
  const left = Math.max(0, (v.duration || 0) - (pr.position || 0));
  return (
    <div className={row} data-testid="sidebar-resume-video" title={v.title}>
      <Link to={`/videos/watch/${v.id}`} className="shrink-0" data-testid="sidebar-resume-video-thumb"><VideoThumb id={v.id} hasThumb={v.has_thumb} className="!h-9 !w-14 !rounded-md" /></Link>
      <Link to={`/videos/watch/${v.id}`} className="min-w-0 flex-1 leading-tight">
        <span className="block truncate text-[9px] font-semibold uppercase tracking-wide text-zinc-400"><Clapperboard size={9} className="mr-1 inline-block align-[-1px]" />{epCode(v) ? `${epCode(v)} · ` : ""}{Math.round(pr.pct)}%{v.duration ? ` · ${fmtClock(left)} left` : ""}</span>
        <span className="block truncate text-xs font-medium text-zinc-900" data-testid="sidebar-resume-video-title">{v.title}</span>
      </Link>
      <button onClick={() => nav(`/videos/watch/${v.id}`)} className={rowBtn} aria-label="Resume" title="Resume" data-testid="sidebar-resume-video-button"><Play size={11} fill="currentColor" /></button>
    </div>
  );
};

/* Sidebar block: the audiobook and video you were in the middle of, pinned under the navigation. */
export const SidebarResume = () => {
  const { book, video } = useContinue();
  if (!book && !video) return null;
  return (
    <div className="mt-2 border-t border-zinc-200/80 pt-2" data-testid="sidebar-resume">
      <p className="sidebar-label flex items-center gap-1.5 px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400"><History size={10} />Resume</p>
      <div className="space-y-0.5">
        {book && <BookRow book={book} />}
        {video && <VideoRow v={video} />}
      </div>
    </div>
  );
};
